import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Loader2, ChevronDown, X } from "lucide-react";

const itDate = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const punti = (r) => (r.punti_spesi != null ? r.punti_spesi : (r.importo || 0) / 120);
const cleanName = (r) => r.prodotto_nome || (r.descrizione || "").replace(/^Prenotazione:\s*/i, "").replace(/^Riserva:\s*/i, "") || "—";

export default function AzioniMarketing() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const canManage = ["admin", "head_of_partner"].includes(user?.role);
  const [rows, setRows] = useState(null);
  const [expanded, setExpanded] = useState({});
  const [busyId, setBusyId] = useState(null);

  const load = () => api.get("/marketing").then((r) => setRows(r.data || [])).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const totPunti = rows ? rows.reduce((s, r) => s + punti(r), 0) : 0;
  const nPren = rows ? rows.filter((r) => r.is_prenotazione).length : 0;

  const cancel = async (r) => {
    if (!window.confirm(`Annullare la prenotazione «${cleanName(r)}» di ${r.partner_name}? I ${punti(r).toLocaleString("it-IT", { maximumFractionDigits: 1 })} Punti Vesta verranno restituiti al saldo.`)) return;
    setBusyId(r.id);
    try {
      const res = await api.delete(`/marketing/${r.id}`);
      toast.success(res.data?.message || "Prenotazione annullata");
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Errore"); }
    finally { setBusyId(null); }
  };

  const colSpan = canManage ? 7 : 6;

  return (
    <div className="space-y-6" data-testid="azioni-marketing-page">
      <div>
        <h1 className="font-heading text-3xl font-black tracking-tight">Prodotti/Servizi prenotati</h1>
        <p className="text-sm text-muted-foreground">Elenco delle prenotazioni effettuate dai partner con i Punti Vesta.</p>
      </div>

      <Card className="p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-heading text-lg font-bold tracking-tight">Elenco prenotazioni</h2>
          {rows && rows.length > 0 && <span className="text-sm text-muted-foreground" data-testid="marketing-total">{nPren} prenotazioni · {totPunti.toLocaleString("it-IT", { maximumFractionDigits: 1 })} Punti Vesta spesi</span>}
        </div>
        {rows === null ? (
          <div className="flex justify-center py-16"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Partner</TableHead>
                  <TableHead>Prodotto / Servizio</TableHead>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Autore</TableHead>
                  <TableHead className="text-right text-[#E3B341]">Punti Vesta</TableHead>
                  {canManage && <TableHead className="text-right">Azioni</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} data-testid={`marketing-row-${r.id}`}>
                    <TableCell className="whitespace-nowrap text-sm">{itDate(r.data)}</TableCell>
                    <TableCell className="text-sm">{r.partner_id ? <Link to={`/partners/${r.partner_id}`} className="font-semibold hover:text-primary hover:underline">{r.partner_name}</Link> : <span className="text-muted-foreground">{r.partner_name || "—"}</span>}</TableCell>
                    <TableCell className="text-sm">
                      <div className="font-medium">{cleanName(r)}</div>
                      {r.prodotto_descrizione && (
                        <>
                          <button type="button" onClick={() => setExpanded((e) => ({ ...e, [r.id]: !e[r.id] }))} className="mt-0.5 flex items-center gap-1 text-xs font-semibold text-primary hover:underline" data-testid={`desc-toggle-${r.id}`}>
                            <ChevronDown className={`h-3 w-3 transition-transform ${expanded[r.id] ? "rotate-180" : ""}`} />
                            {expanded[r.id] ? "Nascondi" : "Descrizione"}
                          </button>
                          {expanded[r.id] && <p className="mt-1 max-w-md text-xs text-muted-foreground" data-testid={`desc-text-${r.id}`}>{r.prodotto_descrizione}</p>}
                        </>
                      )}
                    </TableCell>
                    <TableCell className="text-sm">{r.tipo ? <Badge variant="outline" className="text-[10px]">{r.tipo === "servizio" ? "Servizio" : "Prodotto"}</Badge> : <span className="text-muted-foreground">—</span>}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">{r.autore || "—"}</TableCell>
                    <TableCell className="text-right font-mono font-semibold text-[#E3B341]" data-testid={`marketing-punti-${r.id}`}>{punti(r).toLocaleString("it-IT", { maximumFractionDigits: 1 })}</TableCell>
                    {canManage && (
                      <TableCell className="text-right">
                        {r.is_prenotazione ? (
                          <Button variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10 hover:text-red-300" onClick={() => cancel(r)} disabled={busyId === r.id} data-testid={`cancel-prenotazione-${r.id}`}>
                            {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <><X className="mr-1 h-4 w-4" /> Annulla</>}
                          </Button>
                        ) : <span className="text-xs text-muted-foreground">—</span>}
                      </TableCell>
                    )}
                  </TableRow>
                ))}
                {rows.length === 0 && <TableRow><TableCell colSpan={colSpan} className="py-12 text-center text-muted-foreground">{isAdmin ? "Nessuna prenotazione registrata." : "Nessuna prenotazione per i tuoi partner."}</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>
        )}
      </Card>
    </div>
  );
}
